import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Search, FileCheck, Settings, TrendingUp, Shield, Clock, Building, Users, Globe, Landmark } from "lucide-react";
import { useLanguage } from "@/hooks/useLanguage";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import developmentsBackground from "@/assets/developments-background.jpg";
import SkylineHero from "@/components/SkylineHero";
import ScrollToTop from "@/components/ScrollToTop";
import heritageRenovation from "@/assets/heritage-renovation.jpg";
import InlineContactForm from "@/components/InlineContactForm";
// import developmentFacade from "@/assets/development-facade.jpg";

const Developments = () => {
  const [isContactFormOpen, setIsContactFormOpen] = useState(false);
  const { t } = useLanguage();
  const heroAnimation = useScrollAnimation();
  const philosophyAnimation = useScrollAnimation();
  const frameworkAnimation = useScrollAnimation();
  const capabilitiesAnimation = useScrollAnimation();
  
  return (
    <div className="min-h-screen bg-white text-foreground">
      <Header />
      
      {/* Hero Section */}
      <section className="min-h-[60vh] sm:min-h-[70vh] md:min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-muted/20 to-background relative overflow-hidden">
        {/* Skyline drawing animation — matches hotels hero */}
        <SkylineHero />
        
        {/* Subtle grid pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border))_1px,transparent_1px)] bg-[size:64px_64px] opacity-20"></div>
        <div className="absolute -top-32 sm:-top-40 -right-32 sm:-right-40 w-64 h-64 sm:w-80 sm:h-80 bg-primary/5 rounded-full blur-3xl animate-float"></div>
        <div className="absolute -bottom-32 sm:-bottom-40 -left-32 sm:-left-40 w-64 h-64 sm:w-80 sm:h-80 bg-secondary/5 rounded-full blur-3xl animate-float" style={{ animationDelay: '1.5s' }}></div>
        
        {/* Back button */}
        <Link 
          to="/"
          className="absolute top-4 left-4 sm:top-6 sm:left-6 md:top-8 md:left-8 flex items-center text-muted-foreground hover:text-foreground transition-all duration-300 z-10 hover-scale group bg-background/80 backdrop-blur-sm rounded-full px-3 py-2 border border-border/50"
        >
          <ArrowLeft className="mr-1 sm:mr-2 transition-transform duration-300 group-hover:-translate-x-1" size={16} />
          <span className="text-xs sm:text-sm">{t("common.backToHome")}</span>
        </Link>

        <div 
          ref={heroAnimation.ref as any}
          className={`container mx-auto px-4 sm:px-6 relative z-10 ${heroAnimation.isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}
        >
          <div className="max-w-6xl mx-auto text-center">
            <div className="space-y-4 sm:space-y-6 md:space-y-8">
              <div className="space-y-3 sm:space-y-4 md:space-y-6">
                <h1 className="text-xl sm:text-2xl md:text-4xl lg:text-5xl xl:text-6xl font-light leading-[0.9] tracking-tight text-foreground">
                  Brassbell Developments
                </h1>
                <div className="w-12 sm:w-16 md:w-20 lg:w-24 h-px bg-gradient-to-r from-primary to-secondary mx-auto"></div>
              </div>
              <p className="text-sm sm:text-base md:text-lg lg:text-xl font-light text-muted-foreground max-w-3xl mx-auto">
                Redeveloping Real Estate, Redefining Value
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Development Philosophy */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-32 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div 
              ref={philosophyAnimation.ref as any}
              className={`max-w-5xl mx-auto ${philosophyAnimation.isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}
            >
              {/* Header Section */}
              <div className="text-center space-y-4 sm:space-y-6 md:space-y-8 mb-12 sm:mb-16 md:mb-20">
                <h3 className="text-xs sm:text-sm uppercase tracking-[0.2em] text-muted-foreground font-medium">
                  Our Philosophy
                </h3>
                <div className="w-12 sm:w-16 h-px bg-gradient-to-r from-primary to-secondary mx-auto"></div>
                <div className="relative">
                  <div className="absolute -inset-4 sm:-inset-6 md:-inset-8 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-2xl sm:rounded-3xl blur-2xl"></div>
                  <p className="relative text-lg sm:text-xl md:text-2xl lg:text-3xl xl:text-4xl font-light leading-relaxed text-foreground max-w-4xl mx-auto hover-lift transition-all duration-500 p-4 sm:p-6 md:p-8">
                    We believe the most sustainable way to grow is not always by building from the ground up, but by giving existing structures a new life.
                  </p>
                </div>
              </div>

              {/* Main Content - Creative Asymmetric Layout */}
              <div className="space-y-8 sm:space-y-12 md:space-y-16">
                <div className="relative">
                  <div className="absolute top-0 right-0 w-16 h-16 sm:w-32 sm:h-32 bg-primary/5 rounded-full blur-2xl animate-pulse"></div>
                  <p className="text-base sm:text-lg md:text-xl font-light leading-relaxed text-muted-foreground max-w-4xl hover-lift transition-all duration-300 p-4 sm:p-6">
                    Brassbell Developments specializes in identifying under-utilized assets and transforming them into high-performing hospitality destinations.
                  </p>
                </div>

                {/* Three Pillars in Staggered Layout */}
                <div className="space-y-8 sm:space-y-12">
                  {[
                    {
                      title: "Selective Acquisition",
                      description: "We target heritage assets and existing buildings with unique character, prime locations, or untapped potential.",
                      position: "sm:ml-0"
                    },
                    {
                      title: "Transformative Redevelopment", 
                      description: "We specialize in repurposing structures, preserving their history while infusing them with new life.",
                      position: "sm:ml-auto sm:mr-0 sm:max-w-2xl"
                    },
                    {
                      title: "Operational Alignment",
                      description: "Every development is designed with our hospitality arm, ensuring optimization for performance and revenue.",
                      position: "sm:ml-16"
                    }
                  ].map((item, index) => (
                    <div 
                      key={index} 
                      className={`${item.position} max-w-xl relative group hover-lift transition-all duration-500`}
                      style={{ animationDelay: `${index * 0.2}s` }}
                    >
                      <div className="absolute -inset-4 sm:-inset-6 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-xl"></div>
                      <div className="relative space-y-4 sm:space-y-6 p-4 sm:p-6 md:p-8 rounded-2xl border border-transparent group-hover:border-border group-hover:bg-background/80 backdrop-blur-sm transition-all duration-300">
                        <div className="flex items-center gap-3 sm:gap-4">
                          <div className="w-8 sm:w-12 h-px bg-gradient-to-r from-primary to-secondary"></div>
                          <span className="text-xs font-mono text-muted-foreground">0{index + 1}</span>
                        </div>
                        <h4 className="text-lg sm:text-xl md:text-2xl font-light text-foreground">{item.title}</h4>
                        <p className="text-sm sm:text-base text-muted-foreground leading-relaxed">
                          {item.description}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Bottom Statement - Centered with Emphasis */}
                <div className="text-center mt-12 sm:mt-16 md:mt-20">
                  <div className="relative max-w-3xl mx-auto">
                    <div className="absolute -inset-4 sm:-inset-6 md:-inset-8 bg-gradient-to-r from-primary/10 via-secondary/10 to-primary/10 rounded-2xl sm:rounded-3xl blur-2xl animate-pulse"></div>
                    <div className="relative space-y-4 sm:space-y-6 p-4 sm:p-6 md:p-8">
                      <div className="w-16 sm:w-24 h-px bg-gradient-to-r from-primary to-secondary mx-auto"></div>
                      <p className="text-sm sm:text-base md:text-lg text-foreground leading-relaxed font-light">
                        Our focus on redevelopment preserves urban fabric while unlocking hidden value, creating places that are both commercially successful and culturally meaningful.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Development Framework */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-32 bg-gradient-to-br from-background via-muted/10 to-background">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div className="space-y-12 sm:space-y-16 md:space-y-20">
              <div 
                ref={frameworkAnimation.ref as any}
                className={`text-center space-y-4 sm:space-y-6 ${frameworkAnimation.isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}
              >
                <h2 className="text-xs sm:text-sm uppercase tracking-[0.2em] text-muted-foreground font-medium">
                  Disciplined Redevelopment Framework
                </h2>
                <div className="w-12 sm:w-16 h-px bg-gradient-to-r from-primary to-secondary mx-auto"></div>
                <p className="text-sm sm:text-base md:text-lg text-foreground max-w-4xl mx-auto">
                  We apply a rigorous, data-driven methodology to identify, acquire, and reposition high-potential real estate assets. Our framework combines institutional discipline with entrepreneurial agility.
                </p>
              </div>
              
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8 sm:gap-12 md:gap-16">
                {[
                  {
                    number: "01",
                    icon: Search,
                    title: "Targeted Asset Identification",
                    description: "We focus on underutilized or mismanaged properties with strong locational fundamentals and historic or architectural value. Each opportunity is benchmarked against strict financial and operational criteria."
                  },
                  {
                    number: "02", 
                    icon: FileCheck,
                    title: "Comprehensive Due Diligence",
                    description: "Our multidisciplinary team conducts a 360° analysis, covering financial performance, regulatory compliance, structural integrity, and market positioning."
                  },
                  {
                    number: "03",
                    icon: Settings,
                    title: "Adaptive Redevelopment Strategy", 
                    description: "Every asset is repositioned with a tailored strategy — whether conversion, restoration, or adaptive reuse. We leverage best-in-class architects and consultants."
                  },
                  {
                    number: "04",
                    icon: TrendingUp,
                    title: "Value Creation Through Repositioning",
                    description: "By introducing contemporary hospitality concepts and implementing operational efficiencies, we unlock embedded value and create assets that outperform market benchmarks."
                  },
                  {
                    number: "05",
                    icon: Shield,
                    title: "Risk Management & Governance",
                    description: "Each project is structured under robust financial governance, with transparent reporting and clear alignment between stakeholders."
                  },
                  {
                    number: "06",
                    icon: Clock,
                    title: "Long-Term Asset Stewardship",
                    description: "Our methodology does not end at delivery. We remain actively engaged through Brassbell Hospitality Group, ensuring sustainable performance."
                  }
                ].map((item, index) => (
                  <div 
                    key={item.number} 
                    className="space-y-4 sm:space-y-6 hover-lift transition-all duration-300 p-4 sm:p-6 rounded-2xl hover:bg-background hover:shadow-lg border border-transparent hover:border-border"
                    style={{ animationDelay: `${index * 0.1}s` }}
                  >
                    <div className="space-y-3 sm:space-y-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-primary/10">
                          <item.icon className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                        </div>
                        <div className="text-xs sm:text-sm font-mono text-muted-foreground">{item.number}</div>
                      </div>
                      <div className="w-6 sm:w-8 h-px bg-gradient-to-r from-primary to-secondary"></div>
                      <h4 className="text-base sm:text-lg font-light text-foreground">{item.title}</h4>
                    </div>
                    <p className="text-muted-foreground text-xs sm:text-sm leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final Capabilities with Image */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-32 bg-muted/30">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-6xl mx-auto">
            <div 
              ref={capabilitiesAnimation.ref as any}
              className={`grid lg:grid-cols-5 gap-8 sm:gap-12 md:gap-16 lg:gap-20 items-start ${capabilitiesAnimation.isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}
            >
              <div className="lg:col-span-3 space-y-8 sm:space-y-12 md:space-y-16">
                <div className="space-y-6 sm:space-y-8 hover-lift transition-all duration-300 p-4 sm:p-6 rounded-2xl hover:bg-background hover:shadow-lg border border-transparent hover:border-border">
                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <Building className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                      </div>
                      <h3 className="text-xs sm:text-sm uppercase tracking-[0.2em] text-muted-foreground font-medium">
                        Expertise in Heritage and Adaptive Reuse
                      </h3>
                    </div>
                    <div className="w-12 sm:w-16 h-px bg-gradient-to-r from-primary to-secondary"></div>
                  </div>
                  <p className="text-sm sm:text-base md:text-lg leading-relaxed text-foreground">
                    Brassbell is recognized as one of the few operators in the region with true expertise in revitalizing heritage buildings. Our track record shows how we transform outdated assets into contemporary hospitality destinations, balancing preservation with profitability.
                  </p>
                </div>
                
                <div className="space-y-6 sm:space-y-8 hover-lift transition-all duration-300 p-4 sm:p-6 rounded-2xl hover:bg-background hover:shadow-lg border border-transparent hover:border-border">
                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <Users className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                      </div>
                      <h3 className="text-xs sm:text-sm uppercase tracking-[0.2em] text-muted-foreground font-medium">
                        Best-in-Class Partnerships
                      </h3>
                    </div>
                    <div className="w-12 sm:w-16 h-px bg-gradient-to-r from-primary to-secondary"></div>
                  </div>
                  <p className="text-sm sm:text-base md:text-lg leading-relaxed text-foreground">
                    We partner with the region's most respected architects, contractors, and consultants, ensuring that every project is delivered with world-class standards. This collaborative approach allows us to manage complexity and deliver superior returns.
                  </p>
                </div>

                <div className="space-y-6 sm:space-y-8 hover-lift transition-all duration-300 p-4 sm:p-6 rounded-2xl hover:bg-background hover:shadow-lg border border-transparent hover:border-border">
                  <div className="space-y-3 sm:space-y-4">
                    <div className="flex items-center gap-3">
                      <div className="p-2 rounded-lg bg-primary/10">
                        <Globe className="w-4 h-4 sm:w-5 sm:h-5 text-primary" />
                      </div>
                      <h3 className="text-xs sm:text-sm uppercase tracking-[0.2em] text-muted-foreground font-medium">
                        Focused Regional Expansion
                      </h3>
                    </div>
                    <div className="w-12 sm:w-16 h-px bg-gradient-to-r from-primary to-secondary"></div>
                  </div>
                  <p className="text-sm sm:text-base md:text-lg leading-relaxed text-foreground">
                    Our current expansion strategy focuses on Egypt, Saudi Arabia, and the wider MENA region, where demand for innovative hospitality products is outpacing supply. Our approach is tailored to local dynamics while adhering to core redevelopment principles.
                  </p>
                </div>
                
              </div>

              <div className="lg:col-span-2 order-first lg:order-last">
                <div className="lg:sticky lg:top-32">
                  <div className="absolute -inset-2 sm:-inset-4 bg-gradient-to-br from-primary/10 to-secondary/10 rounded-2xl blur-xl animate-pulse"></div>
                  <div className="relative h-[300px] sm:h-[400px] md:h-[500px] lg:h-[700px] bg-muted rounded-2xl overflow-hidden shadow-2xl border border-border hover-lift">
                    <img 
                      src="/lovable-uploads/a5c726a9-ae54-459e-a726-b2cd2e6eaef9.png" 
                      alt="Modern Development Facade - MENA Architecture" 
                      className="w-full h-full object-cover hover:scale-110 transition-transform duration-700" 
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-12 sm:py-16 md:py-20 lg:py-32 bg-gradient-to-br from-primary/5 via-background to-secondary/5 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,hsl(var(--border))_1px,transparent_1px),linear-gradient(to_bottom,hsl(var(--border))_1px,transparent_1px)] bg-[size:32px_32px] opacity-10"></div>
        
        <div className="container mx-auto px-4 sm:px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="space-y-8 sm:space-y-10 md:space-y-12">
              <div className="space-y-6 sm:space-y-8">
                <div className="flex items-center justify-center gap-4 mb-4 sm:mb-6">
                  <div className="p-2 sm:p-3 rounded-xl bg-primary/10 border border-primary/20">
                    <Landmark className="w-6 h-6 sm:w-8 sm:h-8 text-primary" />
                  </div>
                </div>
                
                <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-light leading-tight text-foreground">
                  Shaping Tomorrow's Landmarks
                </h2>
                
                <div className="w-16 sm:w-24 h-px bg-gradient-to-r from-primary to-secondary mx-auto"></div>
                
                <p className="text-sm sm:text-base md:text-lg lg:text-xl font-light leading-relaxed text-muted-foreground max-w-3xl mx-auto">
                  We actively seek collaborations with landlords, investors, funds, and developers who share our vision of transforming real estate into hospitality-driven assets. Brassbell brings the expertise and operational know-how to turn buildings into thriving destinations.
                </p>
              </div>
              
              <div className="flex justify-center items-center">
                <button 
                  onClick={() => setIsContactFormOpen(true)}
                  className="w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-4 bg-primary text-primary-foreground rounded-xl hover:bg-primary/90 transition-all duration-300 hover-scale font-medium text-sm sm:text-base"
                >
                  Start a Collaboration
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ScrollToTop />
      <Footer />
      
      {/* Contact Form Modal */}
      {isContactFormOpen && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-background rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-2xl font-semibold">Start a Collaboration</h3>
                <button 
                  onClick={() => setIsContactFormOpen(false)}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  ✕
                </button>
              </div>
              <InlineContactForm />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Developments;